import axios from 'axios';
import { getAuthToken, saveAuth, logout } from './auth';

const API_URL = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'http://localhost:5000/api');

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15 seconds default timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect to login if user is on a public route
      const publicRoutes = ['/', '/login', '/register', '/email-login', '/terms', '/privacy'];
      const currentPath = window.location.pathname;
      const isPublicRoute =
        publicRoutes.includes(currentPath) ||
        currentPath.startsWith('/babysitter/') ||
        currentPath.startsWith('/job/');
      
      // Only redirect if not on a public route
      if (!isPublicRoute) {
        logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const loginWithEmail = async (email, registrationData = null) => {
  try {
    const payload = { email };
    if (registrationData) {
      payload.registrationData = registrationData;
    }
    const response = await api.post('/auth/send-login-link', payload, {
      timeout: 10000, // 10 seconds timeout
    });
    return { success: true, message: response.data.message };
  } catch (error) {
    // Handle different types of errors
    let errorMessage = 'Greška pri slanju linka';
    
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      errorMessage = 'Zahtev je istekao. Molimo pokušajte ponovo.';
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      // Network error - server not reachable or CORS issue
      if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        errorMessage = 'Ne možemo se povezati sa serverom. Proverite internet konekciju ili pokušajte kasnije.';
      } else {
        errorMessage = 'Server nije dostupan. Molimo pokušajte kasnije.';
      }
    } else if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      if (status === 503) {
        errorMessage = error.response.data?.message || 'Usluga privremeno nedostupna. Molimo pokušajte kasnije.';
      } else if (status === 500) {
        errorMessage = 'Greška na serveru. Molimo pokušajte kasnije.';
      } else if (status === 400) {
        errorMessage = error.response.data?.message || 'Nevalidan zahtev. Proverite email adresu.';
      } else if (status === 502) {
        errorMessage = error.response.data?.message || 'Server je privremeno nedostupan. Pokušajte ponovo za nekoliko trenutaka.';
      } else {
        errorMessage = error.response.data?.message || `Greška pri slanju linka (${status}).`;
      }
    } else {
      errorMessage = error.message || 'Nepoznata greška. Molimo pokušajte ponovo.';
    }
    
    console.error('Login link error:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

export const loginWithPassword = async (email, password) => {
  try {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.token && response.data.user) {
      saveAuth(response.data.token, response.data.user);
      return { success: true, user: response.data.user };
    }
    return { success: false, error: response.data?.message || 'Nevažeći odgovor' };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Pogrešni podaci za prijavu',
    };
  }
};

/** Register with email + password. Payload: firstName, lastName, email, password, role, phone, address?, preferences? */
export const registerWithPassword = async (payload) => {
  try {
    const response = await api.post('/auth/register', payload);
    if (response.data.token && response.data.user) {
      saveAuth(response.data.token, response.data.user);
      return { success: true, user: response.data.user };
    }
    return { success: false, error: response.data?.message || 'Nevažeći odgovor' };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Greška pri registraciji',
    };
  }
};

export const verifyLoginLink = async (email, token, emailHash) => {
  try {
    const response = await api.post('/auth/verify-login-link', {
      email,
      token,
      email_hash: emailHash,
    });
    if (response.data.token && response.data.user) {
      saveAuth(response.data.token, response.data.user);
      return { success: true, user: response.data.user };
    }
    return { success: false, error: 'Invalid response' };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Greška pri verifikaciji',
    };
  }
};

export const loginWithGoogle = async (idToken, accessToken, email, displayName, photoUrl, registrationData = null) => {
  try {
    const response = await api.post('/auth/google', {
      idToken,
      accessToken,
      email,
      displayName,
      photoUrl,
      registrationData,
    });
    if (response.data.token && response.data.user) {
      saveAuth(response.data.token, response.data.user);
      return { success: true, user: response.data.user };
    }
    return { success: false, error: 'Invalid response' };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Greška pri Google prijavi',
    };
  }
};

export const getPaymentReminderSettings = async () => {
  const response = await api.get('/settings/reminders');
  return response.data;
};

export const updatePaymentReminderSettings = async (settings) => {
  const response = await api.put('/settings/reminders', settings);
  return response.data;
};

export const runPaymentRemindersNow = async () => {
  const response = await api.post('/settings/run-reminders');
  return response.data;
};

export const getPaymentReminderSettingsForFriend = async (friendId) => {
  const response = await api.get(`/friends/${friendId}/reminders`);
  return response.data;
};

export const updatePaymentReminderSettingsForFriend = async (friendId, settings) => {
  const response = await api.put(`/friends/${friendId}/reminders`, settings);
  return response.data;
};

export const updateUserProfile = async (payload) => {
  // Handle FormData if photo is included, otherwise send JSON
  const config = {};
  if (payload.photo && payload.photo instanceof File) {
    const formData = new FormData();
    Object.keys(payload).forEach(key => {
      if (key === 'photo') {
        formData.append('photo', payload.photo);
      } else {
        formData.append(key, typeof payload[key] === 'object' ? JSON.stringify(payload[key]) : payload[key]);
      }
    });
    config.headers = { 'Content-Type': 'multipart/form-data' };
    const response = await api.put('/auth/profile', formData, config);
    const token = getAuthToken();
    if (token && response.data?.user) {
      saveAuth(token, response.data.user);
    }
    return response.data;
  } else {
    const response = await api.put('/auth/profile', payload);
    const token = getAuthToken();
    if (token && response.data?.user) {
      saveAuth(token, response.data.user);
    }
    return response.data;
  }
};

export const sendOTPCode = async (email) => {
  try {
    const response = await api.post('/auth/send-otp', { email });
    return { success: true, message: response.data.message };
  } catch (error) {
    let errorMessage = 'Greška pri slanju OTP koda';
    
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      errorMessage = 'Zahtev je istekao. Molimo pokušajte ponovo.';
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      errorMessage = 'Ne možemo se povezati sa serverom. Proverite internet konekciju.';
    } else if (error.response) {
      const status = error.response.status;
      if (status === 503) {
        errorMessage = error.response.data?.message || 'Usluga privremeno nedostupna.';
      } else if (status === 403) {
        // Specific handling for 403 (authentication failure)
        errorMessage = error.response.data?.message || 'Problem sa email servisom. Molimo kontaktirajte administratora ili pokušajte kasnije.';
      } else if (status === 500) {
        errorMessage = error.response.data?.message || 'Greška na serveru. Molimo pokušajte kasnije.';
      } else if (status === 400) {
        errorMessage = error.response.data?.message || error.response.data?.errors?.[0]?.msg || 'Nevalidan email.';
      } else {
        errorMessage = error.response.data?.message || `Greška pri slanju OTP koda (${status}).`;
      }
    }
    
    console.error('Send OTP error:', error);
    console.error('Error response:', error.response?.data);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

export const verifyOTPCode = async (email, code) => {
  try {
    const response = await api.post('/auth/verify-otp', { email, code });
    if (response.data.token && response.data.user) {
      saveAuth(response.data.token, response.data.user);
      return { success: true, user: response.data.user };
    }
    return { success: false, error: 'Invalid response' };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Greška pri verifikaciji OTP koda',
    };
  }
};

export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return { success: true, message: response.data?.message };
  } catch (error) {
    let errorMessage = 'Greška pri slanju linka za reset.';
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      errorMessage = 'Zahtev je istekao. Molimo pokušajte ponovo.';
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      errorMessage = 'Ne možemo se povezati sa serverom. Proverite internet konekciju.';
    } else if (error.response) {
      const status = error.response.status;
      if (status === 503) {
        errorMessage = error.response.data?.message || 'Reset lozinke trenutno nije dostupan. Molimo kontaktirajte podršku.';
      } else {
        errorMessage = error.response.data?.message || `Greška (${status}). Molimo pokušajte kasnije.`;
      }
    }
    return { success: false, error: errorMessage };
  }
};

export const resetPasswordWithToken = async (email, token, password) => {
  try {
    const response = await api.post('/auth/reset-password', { email, token, password });
    return { success: true, message: response.data?.message };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Greška pri resetovanju lozinke.',
    };
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get('/auth/me');
    const token = getAuthToken();
    const responseUser = response.data?.user || response.data;
    if (token && responseUser) {
      saveAuth(token, responseUser);
    }
    return { success: true, user: responseUser };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default api;

